import axios from 'axios';
import apiConfig from '../../config/apiConfig.js';

// Type definitions
export interface State {
    _id: string;
    name: string;
    code?: string;
}

export interface City {
    _id: string;
    name: string;
    state?: string;
    stateCode?: string;
    stateId?: number;
    createdAt?: string;
    updatedAt?: string;
}

// API Response types
interface StateResponse {
    status: string;
    message?: string;
    states?: State[];
}

interface CityResponse {
    status: string;
    message?: string;
    cities?: City[];
}

// Location API class
export class LocationApi {
    private getAuthHeaders() {
        const authToken = localStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            ...(authToken && { Authorization: `Bearer ${authToken}` })
        };
    }

    async getAllStates(): Promise<StateResponse> {
        try {
            const response = await axios.get(
                `${apiConfig.apiBaseURL}/get-all-states`,
                {
                    headers: this.getAuthHeaders()
                }
            );
            return response.data;
        } catch (error: any) {
            console.error('Error fetching states:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch states');
        }
    }

    async getAllCities(): Promise<CityResponse> {
        try {
            const response = await axios.get(
                `${apiConfig.apiBaseURL}/get-all-cities`,
                {
                    headers: this.getAuthHeaders()
                }
            );
            return response.data;
        } catch (error: any) {
            console.error('Error fetching cities:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch cities');
        }
    }

    async getCitiesByStateCodeFromAPI(stateCode: string): Promise<CityResponse> {
        try {
            const response = await axios.get(
                `${apiConfig.apiBaseURL}/get-all-cities/${stateCode}`,
                {
                    headers: this.getAuthHeaders()
                }
            );
            return response.data;
        } catch (error: any) {
            console.error('Error fetching cities by state code:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch cities');
        }
    }

    async getCitiesByStateCode(stateCode: string): Promise<CityResponse> {
        try {
            // Use the new API endpoint that accepts stateCode parameter
            return await this.getCitiesByStateCodeFromAPI(stateCode);
        } catch (error: any) {
            console.error('Error fetching cities by state code:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to fetch cities');
        }
    }

    async getCitiesByState(stateName: string): Promise<CityResponse> {
        try {
            // Get all cities from your backend
            const allCitiesResponse = await this.getAllCities();
            
            if (allCitiesResponse.status !== 'Success' || !allCitiesResponse.cities) {
                throw new Error('Failed to fetch cities');
            }

            // Filter cities by state name (client-side filtering)
            const filteredCities = allCitiesResponse.cities.filter(city => 
                city.state && city.state.toLowerCase() === stateName.toLowerCase()
            );

            return {
                status: 'Success',
                message: `Cities found for ${stateName}`,
                cities: filteredCities
            };
        } catch (error: any) {
            console.error('Error fetching cities by state:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to fetch cities');
        }
    }

    async getCitiesByStateId(stateId: string): Promise<CityResponse> {
        try {
            // First get all states to find the state code
            const allStatesResponse = await this.getAllStates();

            if (allStatesResponse.status !== 'Success' || !allStatesResponse.states) {
                throw new Error('Failed to fetch states');
            }

            // Find the state by ID to get its code
            const state = allStatesResponse.states.find(s => s._id === stateId);
            if (!state) {
                throw new Error(`State with ID ${stateId} not found`);
            }

            // Use state code with the new API endpoint if available
            if (state.code) {
                return await this.getCitiesByStateCodeFromAPI(state.code);
            } else {
                // Fallback: if no state code, use the old method with client-side filtering
                const allCitiesResponse = await this.getAllCities();
                if (allCitiesResponse.status !== 'Success' || !allCitiesResponse.cities) {
                    throw new Error('Failed to fetch cities');
                }

                const filteredCities = allCitiesResponse.cities.filter(city =>
                    city.state && city.state.toLowerCase() === state.name.toLowerCase()
                );

                return {
                    status: 'Success',
                    message: `Cities found for ${state.name}`,
                    cities: filteredCities
                };
            }
        } catch (error: any) {
            console.error('Error fetching cities by state ID:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to fetch cities');
        }
    }

    async searchCities(cityName: string): Promise<CityResponse> {
        try {
            const allCitiesResponse = await this.getAllCities();
            
            if (allCitiesResponse.status !== 'Success' || !allCitiesResponse.cities) {
                throw new Error('Failed to fetch cities');
            }

            // Filter cities by name (case-insensitive search)
            const filteredCities = allCitiesResponse.cities.filter(city => 
                city.name.toLowerCase().includes(cityName.toLowerCase())
            );

            return {
                status: 'Success',
                message: `Cities found matching "${cityName}"`,
                cities: filteredCities
            };
        } catch (error: any) {
            console.error('Error searching cities:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to search cities');
        }
    }
}

// Export singleton instance
export const locationApi = new LocationApi();